import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ActionBar, Button, Modal, Select, Text } from '@viapeople/components'
import { useEffect, useState } from 'react'
import { GenericTable } from '../../../components/GenericTable'
import { useToast } from '../../../utils/ToastContext'
import { trpc } from '../../../utils/trpc'
import type {
	AssignableParticipant,
	AssignableParticipantSearchInput,
	ViewerAssignment,
	ViewerAssignmentSearchInput,
} from './types'

type ViewerScopeModalProps = {
	isOpen: boolean
	onClose: () => void
	viewer: {
		id: string
		userId: string
		cycleId: string
		scopeType: 'all' | 'hierarchy' | 'assigned'
		firstName: string
		lastName: string
	}
	organizationId: string
	cycleId: string
}

export function ViewerScopeModal({ isOpen, onClose, viewer, organizationId, cycleId }: ViewerScopeModalProps) {
	const [scopeType, setScopeType] = useState<'all' | 'hierarchy' | 'assigned'>(viewer.scopeType)
	const [selectedParticipants, setSelectedParticipants] = useState<AssignableParticipant[]>([])
	const [selectedAssignments, setSelectedAssignments] = useState<ViewerAssignment[]>([])
	const [showAddParticipants, setShowAddParticipants] = useState(false)

	const queryClient = useQueryClient()
	const { showToast } = useToast()

	useEffect(() => {
		setScopeType(viewer.scopeType)
	}, [viewer.scopeType])

	const updateScopeMutation = useMutation({
		...trpc.admin.cyclesViewers.updateScope.mutationOptions(),
		onSuccess: () => {
			showToast('Viewer scope updated')
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cyclesViewers.search.queryKey(),
				exact: false,
				refetchType: 'all',
			})
			onClose()
		},
		onError: () => {
			showToast('Failed to update viewer scope', 'error')
		},
	})

	const setAssignmentsMutation = useMutation({
		...trpc.admin.cyclesViewers.setAssignments.mutationOptions(),
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cyclesViewers.getAssignments.queryKey(),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cyclesViewers.searchAssignableParticipants.queryKey(),
				exact: false,
				refetchType: 'all',
			})
			setShowAddParticipants(false)
			setSelectedParticipants([])
		},
		onSuccess: () => {
			showToast('Participants assigned')
		},
		onError: () => {
			showToast('Failed to assign participants', 'error')
		},
	})

	const removeAssignmentsMutation = useMutation({
		...trpc.admin.cyclesViewers.removeAssignments.mutationOptions(),
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cyclesViewers.getAssignments.queryKey(),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cyclesViewers.searchAssignableParticipants.queryKey(),
				exact: false,
				refetchType: 'all',
			})
		},
		onSuccess: () => {
			showToast('Participants removed')
			setSelectedAssignments([])
		},
		onError: () => {
			showToast('Failed to remove participants', 'error')
		},
	})

	const handleSave = () => {
		updateScopeMutation.mutate({
			organizationId,
			cycleId,
			cycleViewerId: viewer.id,
			scopeType,
		})
	}

	const handleAddParticipants = () => {
		setAssignmentsMutation.mutate({
			organizationId,
			cycleId,
			cycleViewerId: viewer.id,
			participantUserIds: selectedParticipants.map((p) => p.userId),
		})
	}

	const handleRemoveAssignments = () => {
		removeAssignmentsMutation.mutate({
			organizationId,
			cycleId,
			cycleViewerId: viewer.id,
			participantUserIds: selectedAssignments.map((a) => a.participantUserId),
		})
	}

	return (
		<Modal isOpen={isOpen} onClose={onClose} fullContent title='Configure Viewer Scope'>
			<div className='p-6 space-y-6'>
				<div>
					<Text variant='small' weight='semibold'>
						Viewer
					</Text>
					<Text variant='small' weight='regular'>
						{viewer.firstName} {viewer.lastName}
					</Text>
				</div>

				<div>
					<Text variant='small' weight='semibold'>
						Scope Type
					</Text>
					<Select
						options={[
							{ value: 'all', label: 'All (default)' },
							{ value: 'hierarchy', label: 'Hierarchy' },
							{ value: 'assigned', label: 'Assigned' },
						]}
						value={scopeType}
						onChange={(value: string) => setScopeType(value as 'all' | 'hierarchy' | 'assigned')}
					/>
				</div>

				{scopeType === 'assigned' && (
					<div className='space-y-4'>
						<div className='flex items-center justify-between'>
							<Text variant='small' weight='semibold'>
								Assigned Participants
							</Text>
							<Button variant='tertiary' size='small' onClick={() => setShowAddParticipants(true)}>
								Add Participants
							</Button>
						</div>

						<div className='border border-neutral-100 rounded-lg'>
							<GenericTable<ViewerAssignment, ViewerAssignmentSearchInput>
								queryOptionsGenerator={(options) => ({
									...trpc.admin.cyclesViewers.getAssignments.queryOptions({
										...options,
										organizationId,
										cycleId,
										cycleViewerId: viewer.id,
									}),
									enabled: isOpen && scopeType === 'assigned',
								})}
								selectable
								onSelectionChange={setSelectedAssignments}
								columns={[
									{
										field: 'firstName',
										label: 'Name',
										colSpan: 2,
										render: (item) => (
											<Text variant='xsmall' weight='regular'>
												{item.firstName} {item.lastName}
											</Text>
										),
									},
									{
										field: 'externalId',
										label: 'ID',
										colSpan: 1,
									},
								]}
								showPagination
								showSearch
								emptyState={{
									title: 'No participants assigned',
									description: "Add participants to restrict this viewer's access",
								}}
							/>
						</div>

						{selectedAssignments.length > 0 && (
							<div className='flex justify-end'>
								<Button variant='destructive' size='small' onClick={handleRemoveAssignments}>
									Remove selected ({selectedAssignments.length})
								</Button>
							</div>
						)}
					</div>
				)}
			</div>

			<ActionBar
				variant='modal'
				className='left-0 flex justify-end'
				rightActions={[
					{ label: 'Cancel', variant: 'secondary', onClick: onClose },
					{ label: 'Save', variant: 'primary', onClick: handleSave },
				]}
			/>

			{showAddParticipants && (
				<Modal
					isOpen={showAddParticipants}
					onClose={() => {
						setShowAddParticipants(false)
						setSelectedParticipants([])
					}}
					fullContent
					fullScreen
					title='Add Participants'>
					<div className='flex flex-col h-[calc(100vh-150px)]'>
						<GenericTable<AssignableParticipant, AssignableParticipantSearchInput>
							queryOptionsGenerator={(options) => ({
								...trpc.admin.cyclesViewers.searchAssignableParticipants.queryOptions({
									...options,
									organizationId,
									cycleId,
									cycleViewerId: viewer.id,
								}),
								enabled: showAddParticipants,
							})}
							isInternal
							selectable
							onSelectionChange={setSelectedParticipants}
							columns={[
								{
									field: 'firstName',
									label: 'Name',
									colSpan: 2,
									render: (item) => (
										<Text variant='xsmall' weight='regular'>
											{item.firstName} {item.lastName}
										</Text>
									),
								},
								{
									field: 'externalId',
									label: 'ID',
									colSpan: 1,
								},
								{
									field: 'email',
									label: 'Email',
									colSpan: 2,
								},
							]}
						/>
					</div>
					<ActionBar
						variant='modal'
						className='left-0 flex justify-end'
						rightActions={[
							{
								label: 'Cancel',
								variant: 'secondary',
								onClick: () => {
									setShowAddParticipants(false)
									setSelectedParticipants([])
								},
							},
							{
								label: `Add (${selectedParticipants.length})`,
								variant: 'primary',
								onClick: handleAddParticipants,
								disabled: selectedParticipants.length === 0,
							},
						]}
					/>
				</Modal>
			)}
		</Modal>
	)
}
